import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { PaymentReceiptTypeViewStore } from "../../store";
import { PaymentReceiptTypeView } from "./PaymentReceiptTypeView";
import { PaymentReceiptTypeUpdateStore } from "../../store";
import { PaymentReceiptTypeUpdate } from "../update/PaymentReceiptTypeUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type PaymentReceiptTypeDrawerContentProps = {
  entityId: string;
  mode?: "view" | "update";
};

export const PaymentReceiptTypeDrawerContent: React.FC<PaymentReceiptTypeDrawerContentProps> =
  observer(({ entityId, mode = "view" }) => {
    const rootStore = useRootStore();
    const viewInitializedRef = useRef<string | null>(null);
    const updateInitializedRef = useRef<string | null>(null);

    const viewStore = useMemo(() => {
      const s = new PaymentReceiptTypeViewStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "view" &&
        entityId &&
        viewInitializedRef.current !== entityId
      ) {
        viewInitializedRef.current = entityId;
        viewStore.initialize({ id: entityId });
      }
    }, [entityId, mode, viewStore]);

    const updateStore = useMemo(() => {
      const s = new PaymentReceiptTypeUpdateStore(rootStore);
      s.isDrawerMode = true;
      return s;
    }, [rootStore]);

    useEffect(() => {
      if (
        mode === "update" &&
        entityId &&
        updateInitializedRef.current !== entityId
      ) {
        updateInitializedRef.current = entityId;
        updateStore.initialize({ id: entityId });
      }
    }, [entityId, mode, updateStore]);

    if (mode === "update") {
      if (
        updateStore.status === "processing" ||
        updateStore.status === "uninitialized"
      ) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        );
      }
      return (
        <PaymentReceiptTypeUpdate paymentReceiptTypeUpdateStore={updateStore} />
      );
    }

    if (
      viewStore.status === "processing" ||
      viewStore.status === "uninitialized"
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return <PaymentReceiptTypeView paymentReceiptTypeViewStore={viewStore} />;
  });
